import type { Evaluation, LocalEvent } from "./types";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export type SessionUser = {
  displayName: string;
  loginMethod: "feishu" | "invite";
  userCode: string;
};

export type SessionResponse = {
  authenticated: boolean;
  expiresAt?: string;
  user?: SessionUser;
};

export class ApiError extends Error {
  constructor(public readonly code: string, public readonly status: number, public readonly detail?: unknown) {
    super(code);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    cache: init.cache ?? "no-store",
    credentials: "include",
    headers: {
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...init.headers,
    },
  });
  const payload = await response.json().catch(() => ({})) as { error?: string; detail?: unknown };
  if (!response.ok) throw new ApiError(payload.error || "request_failed", response.status, payload.detail);
  return payload as T;
}

export const api = {
  authProviders: () => request<{ feishu: { configured: boolean } }>("/api/auth/providers"),
  prepareFeishuLogin: () => request<{ authorizeUrl: string }>("/api/auth/feishu/prepare"),
  session: () => request<SessionResponse>("/api/session"),
  login: (inviteCode: string) => request<SessionResponse>("/api/session", {
    method: "POST",
    body: JSON.stringify({ inviteCode }),
  }),
  logout: () => request<{ authenticated: boolean }>("/api/session", { method: "DELETE" }),
  listEvaluations: () => request<{ evaluations: Evaluation[] }>("/api/evaluations"),
  getEvaluation: (id: string) => request<{ evaluation: Evaluation }>(`/api/evaluations/${encodeURIComponent(id)}`),
  createEvaluation: (input: {
    productName: string;
    targetUrl: string;
    taskQuery: string;
    experienceBrief: string;
    scenarioName: string;
    personaName: string;
    monitoringProfile: "generic" | "nexplay";
    responseDeadlineSeconds: number;
  }) => request<{ evaluation: Evaluation; relay: { sessionId: string; pairingCode: string; expiresAt: string } }>(
    "/api/evaluations",
    { method: "POST", body: JSON.stringify(input) },
  ),
  startEvaluation: (id: string) => request<{ evaluation: Evaluation }>(
    `/api/evaluations/${encodeURIComponent(id)}/start`,
    { method: "POST", body: "{}" },
  ),
  retryEvaluation: (id: string) => request<{ evaluation: Evaluation; retryMode: "evaluator" | "full"; relay?: { sessionId: string; pairingCode: string; expiresAt: string } }>(
    `/api/evaluations/${encodeURIComponent(id)}/retry`,
    { method: "POST", body: "{}" },
  ),
  stopEvaluation: (id: string, reason = "用户在 EchoLab 控制台手动停止") => request<{ evaluation: Evaluation }>(
    `/api/evaluations/${encodeURIComponent(id)}/stop`,
    { method: "POST", body: JSON.stringify({ reason }) },
  ),
  events: (id: string) => request<{ local: LocalEvent[]; simulator: unknown; evaluator: unknown; maxwell: unknown }>(
    `/api/evaluations/${encodeURIComponent(id)}/events?localOnly=1`,
  ),
};

export function readableError(error: unknown): string {
  const code = error instanceof ApiError ? error.code : "request_failed";
  return {
    invite_code_invalid: "邀请码不正确，请重新输入。",
    feishu_access_denied: "你取消了飞书授权，可以重新尝试或使用邀请码登录。",
    feishu_code_missing: "飞书没有返回登录凭证，请重新尝试。",
    feishu_login_failed: "飞书登录没有完成，请稍后重试或使用邀请码登录。",
    feishu_not_configured: "飞书登录正在配置中，请暂时使用邀请码。",
    feishu_state_invalid: "飞书登录验证已过期，请重新发起登录。",
    feishu_tenant_not_allowed: "当前飞书企业暂未获得 EchoLab 使用权限。",
    authentication_required: "登录已过期，请重新登录。",
    session_invalid_or_expired: "登录已过期，请重新登录。",
    target_url_invalid: "请输入完整的 http 或 https 网页地址。",
    target_url_private_network_not_allowed: "托管模式暂不支持本地或内网地址。",
    browser_extension_not_connected: "浏览器插件尚未连接到这个评测会话。",
    evaluation_is_terminal: "这次评测已经结束，不能再次启动。",
    only_failed_evaluation_can_retry: "只有失败的评测可以原地重试。",
    evaluation_retry_conflict: "这次评测已经被其他操作更新，请刷新后重试。",
    maxwell_request_failed: "Maxwell 暂时无法响应，请稍后重试。",
    maxwell_runtime_failed: "用户模拟 Agent 执行失败，请查看失败阶段后重试。",
    evaluator_start_failed: "用户模拟已完成，但独立评估器启动失败，请重试。",
    evaluator_runtime_failed: "独立评估器执行失败，请重试。",
    evaluation_start_timed_out: "评测启动超时，已安全释放浏览器控制，请重试。",
    relay_request_failed: "浏览器 Relay 暂时无法响应，请检查插件连接。",
    request_failed: "请求没有成功，请检查网络后重试。",
  }[code] || `操作失败: ${code}`;
}
